import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import AdminShell from "../../components/AdminShell";
import StudentWork from "../../components/StudentWork";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  LineChart, Line,
} from "recharts";
import {
  Loader2, Users, CheckCircle2, AlertTriangle, Camera, ClipboardCheck,
  TrendingUp, Inbox, ChevronRight, Sparkles,
} from "lucide-react";

/**
 * How the group is getting on with the work that was actually set.
 *
 * Separate from the Analytics screen on purpose. That one measures the
 * multiple-choice quiz, where a question is right or wrong. This one measures
 * exam questions marked against a mark scheme, where a question is worth four
 * marks and a child got two of them. Putting both on one page would mean one
 * "accuracy" figure that silently meant two different things.
 *
 * Every percentage here is marks the tutor awarded over marks available on the
 * questions the tutor has marked. A child's own self-marking is shown, labelled
 * as theirs, and never folded into the same number - see backend/progress.py.
 */

function pctColour(p) {
  if (p == null) return "#cbd5e1";
  return p >= 80 ? "#15803d" : p >= 60 ? "#a16207" : "#b91c1c";
}

function Kpi({ label, value, sub, icon: Icon, colour = "#64748b" }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-start gap-3"
         style={{ borderTopColor: colour, borderTopWidth: 3 }}>
      <div className="rounded-lg p-2 shrink-0" style={{ background: colour + "18" }}>
        <Icon size={17} style={{ color: colour }} />
      </div>
      <div className="min-w-0">
        <div className="text-xl font-bold text-slate-900">{value}</div>
        <div className="text-xs text-slate-500 mt-0.5">{label}</div>
        {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

export default function AdminProgress() {
  const [d, setD] = useState(null);
  const [groupId, setGroupId] = useState("");
  const [loading, setLoading] = useState(true);
  const [workFor, setWorkFor] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/progress${groupId ? `?group_id=${groupId}` : ""}`)
      .then((r) => setD(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load progress"))
      .finally(() => setLoading(false));
  }, [groupId]);

  useEffect(() => { load(); }, [load]);

  const k = d?.kpis;
  // Only students with a mark go on the comparison chart. A bar at zero for a
  // child nobody has marked yet reads as a child who got nothing right.
  const chart = (d?.students || []).filter((s) => s.tutor_pct != null)
    .map((s) => ({ name: s.student_name, pct: s.tutor_pct, student_id: s.student_id }));

  return (
    <AdminShell title="Progress" subtitle="Homework and tests from the question bank">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <p className="text-sm text-slate-500">
          {loading ? "Loading…" : `${k?.students ?? 0} student${k?.students === 1 ? "" : "s"} · ${k?.assigned ?? 0} worksheet${k?.assigned === 1 ? "" : "s"} given out`}
        </p>
        <select value={groupId} onChange={(e) => setGroupId(e.target.value)}
          className="text-sm border border-slate-300 rounded-lg px-3 py-2 bg-white">
          <option value="">Everyone</option>
          {(d?.groups || []).map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}{g.academic_year ? ` (${g.academic_year})` : ""} · {g.student_count}
            </option>
          ))}
        </select>
      </div>

      {loading && !d && (
        <div className="flex justify-center py-20"><Loader2 size={26} className="animate-spin text-slate-400" /></div>
      )}

      {d && k && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
            <Kpi label="Group average" icon={TrendingUp} colour="#2563eb"
                 value={k.group_pct == null ? "—" : `${k.group_pct}%`}
                 sub={k.students_marked ? `${k.students_marked} student${k.students_marked === 1 ? "" : "s"} marked` : "nothing marked yet"} />
            <Kpi label="Handed in" icon={CheckCircle2} colour="#16a34a"
                 value={`${k.handed_in}/${k.assigned}`} sub={`${k.outstanding} outstanding`} />
            <Kpi label="Overdue" icon={AlertTriangle} colour={k.overdue ? "#dc2626" : "#64748b"}
                 value={k.overdue} sub="past the due date" />
            <Kpi label="Waiting for you" icon={Inbox} colour="#d97706"
                 value={k.awaiting_marking} sub="handed in, not fully marked" />
            <Kpi label="Photos of working" icon={Camera} colour="#7c3aed" value={k.photos} />
            <Kpi label="Students" icon={Users} colour="#0891b2" value={k.students} />
          </div>

          {k.awaiting_marking > 0 && (
            <p className="mt-4 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3.5 py-2.5">
              <strong>{k.awaiting_marking}</strong> piece{k.awaiting_marking === 1 ? "" : "s"} of work
              {k.awaiting_marking === 1 ? " has" : " have"} been handed in and
              {k.awaiting_marking === 1 ? " is" : " are"} not fully marked. Open a student below to read their
              working and give marks.
            </p>
          )}

          <div className="grid lg:grid-cols-2 gap-5 mt-6">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <h3 className="text-xs uppercase tracking-wider font-bold text-slate-400 mb-3">
                Side by side — marks you awarded
              </h3>
              {chart.length === 0 ? (
                <p className="text-sm text-slate-400 py-12 text-center">
                  Nothing is marked yet, so there is nothing to compare.
                </p>
              ) : (
                <div style={{ height: Math.max(170, chart.length * 34) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chart} layout="vertical" margin={{ top: 4, right: 36, bottom: 4, left: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                      <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fill: "#94a3b8" }} />
                      <YAxis type="category" dataKey="name" width={96}
                             tick={{ fontSize: 11, fill: "#475569" }} />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }}
                               formatter={(v) => [`${v}%`, "marks awarded"]} />
                      <Bar dataKey="pct" radius={[0, 4, 4, 0]}>
                        {chart.map((c) => <Cell key={c.student_id} fill={pctColour(c.pct)} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
              <p className="text-[11px] text-slate-400 mt-2">
                Only students with marks appear here &mdash; yours, or questions the checker confirmed outright.
                A child nobody has marked is not a child scoring zero.
              </p>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <h3 className="text-xs uppercase tracking-wider font-bold text-slate-400 mb-3">
                Topics the group finds hardest
              </h3>
              <p className="text-[11px] text-slate-400 -mt-2 mb-3">
                Your marks where you have given them. Where you have not marked yet, the row says so and
                shows the students' own marking instead.
              </p>
              {(d.topics || []).length === 0 ? (
                <p className="text-sm text-slate-400 py-12 text-center">No topic figures yet.</p>
              ) : (
                <div className="space-y-2.5">
                  {d.topics.slice(0, 12).map((t) => (
                    <div key={t.topic}>
                      <div className="flex justify-between items-baseline text-xs">
                        <span className="font-medium text-slate-700 truncate pr-2">{t.topic}</span>
                        <span className="font-bold shrink-0" style={{ color: pctColour(t.pct) }}>{t.pct}%</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden mt-1">
                        <div className="h-full rounded-full" style={{ width: `${t.pct}%`, background: pctColour(t.pct) }} />
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        {t.students} student{t.students === 1 ? "" : "s"} · {t.below_60} below 60% · {t.marks_available} marks
                        {t.source === "self" ? " · students' own marking, not yours" : ""}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {(d.weeks || []).length > 1 && (
            <div className="bg-white rounded-xl border border-slate-200 p-4 mt-5">
              <h3 className="text-xs uppercase tracking-wider font-bold text-slate-400 mb-3">
                Hand-ins by week
              </h3>
              <div className="h-[150px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={d.weeks} margin={{ top: 6, right: 12, bottom: 4, left: -22 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="week" tick={{ fontSize: 10, fill: "#94a3b8" }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94a3b8" }} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }}
                             formatter={(v) => [v, "handed in"]} />
                    <Line type="monotone" dataKey="handed_in" stroke="#1e293b" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <h3 className="text-xs uppercase tracking-wider font-bold text-slate-400 mb-2 mt-6">
            Every student — weakest first
          </h3>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {d.students.length === 0 && (
              <p className="py-14 text-center text-sm text-slate-500">
                No students in this group.
              </p>
            )}
            {d.students.map((s) => (
              <button key={s.student_id} onClick={() => setWorkFor(s.student_id)}
                className="w-full text-left px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50/70 flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="flex-1 min-w-[140px]">
                  <span className="block font-medium text-slate-900">{s.student_name}</span>
                  <span className="block text-[11px] text-slate-400">
                    {s.year ? `Year ${s.year} · ` : ""}{s.assigned} set · {s.handed_in} handed in
                    {s.overdue ? ` · ${s.overdue} overdue` : ""}
                    {s.weakest_topic ? ` · weakest: ${s.weakest_topic}` : ""}
                  </span>
                </span>
                <span className="text-xs inline-flex items-center gap-1 text-slate-500">
                  <Camera size={12} /> {s.photos}
                </span>
                <span className="text-xs inline-flex items-center gap-1 text-slate-500">
                  {s.confirmed > 0 ? <><Sparkles size={12} /> {s.confirmed} auto</>
                                   : <><ClipboardCheck size={12} /> {s.self_pct == null ? "—" : `self ${s.self_pct}%`}</>}
                </span>
                <span className="text-sm font-bold w-[64px] text-right"
                      style={{ color: pctColour(s.tutor_pct) }}>
                  {s.tutor_pct == null ? "unmarked" : `${s.tutor_pct}%`}
                </span>
                <ChevronRight size={14} className="text-slate-300" />
              </button>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-2">
            Open a student to read their working, give marks, and see their own trend over time.
          </p>
        </>
      )}

      {workFor && <StudentWork studentId={workFor} onClose={() => { setWorkFor(null); load(); }} />}
    </AdminShell>
  );
}
